import { Pipe, PipeTransform, inject } from '@angular/core';

import {
  ConversionOptions,
  UnitSystem,
  UnitType,
  convertAbsolute,
  convertDuration,
  convertLength,
  convertPercentage,
  convertSpeed,
  convertWeight,
} from '@optical/utils';

import { UserSettingsStore } from '../state/user-settings';

export type UnitPipeOptions =
  | UnitPipeOptionsForType<UnitType.ABSOLUTE>
  | UnitPipeOptionsForType<UnitType.DURATION>
  | UnitPipeOptionsForType<UnitType.LENGTH>
  | UnitPipeOptionsForType<UnitType.PERCENTAGE>
  | UnitPipeOptionsForType<UnitType.SPEED>
  | UnitPipeOptionsForType<UnitType.WEIGHT>;

export type UnitPipeOptionsForType<TType extends UnitType> = Partial<
  Omit<
    ConversionOptions<TType>,
    // The pipe decides the unit system based on the user's preferences.
    'from' | 'to'
  >
> & {
  unitType: TType;
};

@Pipe({
  name: 'unit',
})
export class UnitPipe implements PipeTransform {
  private readonly userSettings = inject(UserSettingsStore);

  transform(value: number, options: UnitPipeOptions): number | string {
    const unitSystem = this.userSettings.unitSystem();

    // In the app, we always convert from metric to the user's preferred unit system.
    const from = UnitSystem.METRIC;
    const to = unitSystem ?? UnitSystem.METRIC;

    switch (options.unitType) {
      case UnitType.ABSOLUTE:
        return convertAbsolute(value, {
          decimals: options.decimals ?? 0,
          formatter: options.formatter,
        });
      case UnitType.DURATION:
        return convertDuration(value, {
          withSeconds: options.withSeconds ?? true,
          withSymbol: options.withSymbol ?? true,
        });
      case UnitType.LENGTH:
        return convertLength(value, {
          decimals: options.decimals ?? 1,
          formatter: options.formatter,
          from,
          to,
          withSymbol: options.withSymbol ?? true,
        });
      case UnitType.PERCENTAGE:
        return convertPercentage(value, {
          decimals: options.decimals ?? 0,
          withSymbol: options.withSymbol ?? true,
        });
      case UnitType.SPEED:
        return convertSpeed(value, {
          decimals: options.decimals ?? 1,
          to,
          withSymbol: options.withSymbol ?? true,
        });
      case UnitType.WEIGHT:
        return convertWeight(value, {
          decimals: options.decimals ?? 2,
          from,
          to,
          withSymbol: options.withSymbol ?? true,
        });
    }
  }
}
